import { useEffect, useRef, useState } from 'react';
import SupportTheMovement from '../components/SupportTheMovement';
import OfflineDonationForm from '../components/OfflineDonationForm';
import IntlDonationForm from '../components/IntlDonationForm';
import { fetchGeoCountry } from '../api';
import { currencyForCountry } from '../intlCountries';
import { loadFundraiseUp, openFundraiseUpCheckout } from '../fundraiseUp';

// The English donate page — a rebuild of standing-together.org/en/donate-en-3
// (Wix), same design on desktop and mobile, minus the "About Us" button that
// pointed at the retired donate-en-old page.
//
// Three ways to give, per the client's spec:
//   1. Check / wire / DAF — OfflineDonationForm, delivered to the client's
//      webhooks.
//   2. Any currency but USD — IntlDonationForm, the IsraelGives iframe.
//   3. USD — Fundraise Up's checkout modal (fundraiseUp.js). Its own
//      check/DAF/wire link comes back to 1. via /en?offline=1.
// Unlike the Hebrew pages, nothing here is written to Gramsci: the payment
// providers report English-page donations to the client themselves.
//
// English and LTR, so the root overrides the app's dir="rtl" lang="he".

const DEFAULT_CURRENCY = 'USD';
const CONTACT_URL ='https://www.standing-together.org/en/contact-us-en';

const SECTIONS = [
  {
    title: 'What is Friends of Standing-Together?',
    image: '/images/en/section-1.jpg',
    body: (
      <>
        <p className="en-lead">By becoming a monthly supporter and joining Friends of Standing Together, you will:</p>
        <ol className="en-list">
          <li>
            Join the larger global community of Standing Together, strengthening our voice for solidarity and
            peace worldwide
          </li>
          <li>
            Have the opportunity to participate in regular Zoom conversations with members of Standing
            Together’s leadership, and other events and meetings exclusive to Friends of Standing Together
          </li>
          <li>Be invited to join Standing Together supporters&apos; activities and organizing around the world</li>
        </ol>
      </>
    ),
  },
  {
    title: 'We need you!',
    image: '/images/en/section-2.jpg',
    body: (
      <p>
        Israel/Palestine is at a historic junction. The coming days, months, and years will determine the path
        we follow - endless war and internal division, or solidarity and peace. For the latter to be achieved,
        a mass movement must demand it. Building such a movement is an ongoing task, and in order to continue
        to grow and expand our movement, we need ongoing support. Friends of Standing Together contribute
        monthly sums to Standing Together, which enables us to achieve the financial stability necessary to
        achieve real fundamental change and adapt to the changing needs of our society. It means that
        you’re not just there for us in moments of extreme crisis - rather you’re with us for the long run,
        and you’re committed to being a part of the long road that lies ahead of us.
      </p>
    ),
  },
  {
    title: 'Who we are',
    image: '/images/en/section-3.jpg',
    body: (
      <>
        <p>
          Standing Together is a progressive grassroots movement mobilizing Jewish and Palestinian citizens
          of Israel against the occupation and for peace, equality, and social justice. We know that the
          majority have far more in common than that which sets us apart and only a tiny minority benefits
          from the status quo.
        </p>
        <p>
          The future that we want — peace and independence for Israelis and Palestinians, full equality for
          everyone in this land, and true social, economic, and environmental justice — is possible.
        </p>
        <p>
          To achieve this future, we must stand together as a united front: Jewish and Palestinian, secular
          and religious, Mizrahi and Ashkenazi, rural and urban, and people of all genders and sexual
          orientations. As the largest Jewish-Arab grassroots movement in Israel, we are committed to creating
          an alternative to our existing reality and building the political strength to make this
          transformation possible.
        </p>
      </>
    ),
  },
  {
    // The Wix page's dark section. Its "About Us" button is the one the client
    // asked to remove.
    dark: true,
    image: '/images/en/section-4.jpg',
    body: (
      <>
        <p>
          At a time when we are witnessing so much death, destruction, racism, and division in
          Israel/Palestine, as well as deep political division, Islamophobia, antisemitism, and anti-Arab
          racism around the world - we need you now more than ever.
        </p>
        <p>
          Join Friends of Standing Together and spread the word to your family and friends so that our voice
          for peace, equality, and social justice in Israel-Palestine will be heard across the globe.
        </p>
      </>
    ),
  },
];

// The blurb beside the card form, per frequency.
const CARD_ASIDE = {
  once: {
    title: 'Support Standing Together with a one-time donation',
    text: 'Support the fight for peace, equality and social and climate justice in Israel-Palestine.',
  },
  monthly: {
    title: 'Become a Friend of Standing Together',
    text: 'Join the global community of monthly supporters who give Standing Together the stability to '
      + 'keep organizing for peace, equality and social justice — for the long run.',
  },
};

// The panel the URL asks for, if any, cleared from the address bar once read:
//   ?donated=1 — a donor whose payment left the frame (PayPal), sent back by
//                ig-complete.html; this is their only confirmation.
//   ?donate=check-daf — the check/DAF/wire form; the link to send donors who
//                want to give that way.
//   ?offline=1 — the same form. Fundraise Up's USD checkout links here from
//                its "Want to donate via check/DAF/wire transfer?" (set in
//                their dashboard), so both currencies share one form.
//   ?donate=one-time / ?donate=monthly — straight to the one-time form or the
//                Friend of Standing Together (monthly) form, in the visitor's
//                own currency (see the geo effect below; USD: Fundraise Up).
// Returns { mode, direct } — `direct` is the frequency a short link asked for
// ('once' | 'monthly'), or null.
const DIRECT_LINKS = { 'one-time': 'once', monthly: 'monthly' };

function readInitialState() {
  const params = new URLSearchParams(window.location.search);
  let mode = 'choose';
  let direct = null;
  if (params.get('donated') === '1') mode = 'donated';
  else if (params.get('donate') === 'check-daf' || params.get('offline') === '1') mode = 'offline';
  else if (DIRECT_LINKS[params.get('donate')]) direct = DIRECT_LINKS[params.get('donate')];
  else return { mode, direct };
  params.delete('donated');
  params.delete('donate');
  params.delete('offline');
  params.delete('t');
  const query = params.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  return { mode, direct };
}

function scrollBehavior() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

export default function InternationalPage() {
  const topRef = useRef(null);
  const panelRef = useRef(null);
  // USD until the visitor's country says otherwise, or until they pick one
  // themselves. null after the "Other" tile: the form opens with its currency
  // dropdown unpicked.
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const pickedCurrency = useRef(false);
  // choose | offline | card | donated — every mode but 'choose' opens a panel
  // below the chooser, which itself always stays, as on the Wix page.
  const [initial] = useState(readInitialState);
  const [mode, setMode] = useState(initial.mode);
  const [selection, setSelection] = useState(null); // { currency, recurring }

  useEffect(() => {
    document.title = 'Donate | Standing Together';
    // Load the USD checkout up front so it opens without a wait on click.
    loadFundraiseUp();
  }, []);

  // Preselect the visitor's currency from their IP country (USD stays for an
  // unknown or unsupported one). A visitor who already clicked a currency isn't
  // overridden by a slow lookup.
  //
  // A direct link (?donate=one-time / monthly) opens its form once the currency
  // is known, so the donor lands in their own currency rather than a USD form
  // they would have to switch. A visitor who clicked in the meantime has taken
  // over, and the link steps aside.
  useEffect(() => {
    let cancelled = false;
    fetchGeoCountry().then((country) => {
      if (cancelled || pickedCurrency.current) return;
      const code = currencyForCountry(country);
      if (code) setCurrency(code);
      if (initial.direct) choose({ currency: code || DEFAULT_CURRENCY, recurring: initial.direct === 'monthly' });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opening a panel scrolls the donor down to it. Wix opens the same section
  // below the chooser but leaves the page where it was.
  // The currency isn't part of the key: switching it inside the form keeps the
  // donor where they are.
  const panelKey = mode === 'card' && selection ? `card-${selection.recurring}` : mode;
  useEffect(() => {
    if (panelKey !== 'choose' && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    }
  }, [panelKey]);

  // The "Donate here" button below brings the donor back up to the chooser.
  const scrollToTop = () => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
  };

  const showChooser = () => {
    setMode('choose');
    setSelection(null);
    scrollToTop();
  };

  // The "Become a Friend" buttons in the sections skip the chooser: they open
  // the monthly form in the currency already picked (USD: the Fundraise Up
  // modal).
  const becomeAFriend = () => {
    // Already open on this very form: no state change to trigger the scroll.
    if (mode === 'card' && selection && selection.recurring && selection.currency === currency && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      return;
    }
    choose({ currency, recurring: true });
  };

  const choose = (picked) => {
    // USD opens Fundraise Up's modal over the page; every other currency
    // opens our IsraelGives form below the chooser.
    if (picked.currency === 'USD') {
      openFundraiseUpCheckout(picked.recurring);
      return;
    }
    setSelection(picked);
    setMode('card');
  };

  // Switching currency while a card form is open moves that form to the new
  // currency — or, for USD, which is Fundraise Up's, closes it and opens their
  // checkout in the form's frequency.
  const changeCurrency = (code) => {
    pickedCurrency.current = true;
    setCurrency(code);
    if (mode !== 'card' || !selection) return;
    if (code === 'USD') {
      openFundraiseUpCheckout(selection.recurring);
      setMode('choose');
      setSelection(null);
      scrollToTop();
    } else {
      setSelection({ ...selection, currency: code });
    }
  };

  const showOffline = () => {
    setSelection(null);
    setMode('offline');
  };

  return (
    <div className="en-page" dir="ltr" lang="en">
      <div className="en-top" ref={topRef}>
        <SupportTheMovement
          currency={currency}
          onCurrencyChange={changeCurrency}
          onSelect={choose}
          onOtherWays={showOffline}
          selected={mode === 'card' && selection ? selection.recurring : null}
        />
      </div>

      <div ref={panelRef}>
        {mode === 'donated' && (
          <div className="en-offline">
            <div className="en-offline-aside">
              <h2 className="en-offline-title">Thank you for supporting Standing Together</h2>
              <button type="button" className="en-textlink" onClick={showChooser}>Make another donation</button>
            </div>
            <div className="idf idf--done" role="status">
              <h2 className="idf-title">Thank you!</h2>
              <p className="idf-sub">Your donation was received. A receipt is on its way to your email.</p>
            </div>
          </div>
        )}
        {mode === 'card' && selection && (
          <div className="en-offline">
            <div className="en-offline-aside">
              <h2 className="en-offline-title">{CARD_ASIDE[selection.recurring ? 'monthly' : 'once'].title}</h2>
              <p>{CARD_ASIDE[selection.recurring ? 'monthly' : 'once'].text}</p>
              <button type="button" className="en-textlink" onClick={showOffline}>
                To donate via Check/DAF/Wire transfer - click here
              </button>
            </div>
            <IntlDonationForm
              key={String(selection.recurring)}
              currency={selection.currency}
              recurring={selection.recurring}
              onChangeCurrency={changeCurrency}
            />
          </div>
        )}
        {mode === 'offline' && (
          <div className="en-offline">
            <div className="en-offline-aside">
              <h2 className="en-offline-title">Support Standing Together with a one-time donation</h2>
              <p>Support the fight for peace, equality and social and climate justice in Israel-Palestine.</p>
              <button type="button" className="en-textlink" onClick={showChooser}>
                To donate via credit card - click here
              </button>
            </div>
            <OfflineDonationForm onOnlineForm={showChooser} />
          </div>
        )}
      </div>

      {SECTIONS.map((s, i) => (
        <section key={s.image} className={`en-section${i % 2 ? ' en-section--flip' : ''}${s.dark ? ' en-section--dark' : ''}`}>
          <img className="en-section-photo" src={s.image} alt="" loading="lazy" />
          <div className="en-section-text">
            {s.title && <h2 className="en-section-title">{s.title}</h2>}
            {s.body}
            <button type="button" className="en-btn" onClick={becomeAFriend}>Become a Friend</button>
          </div>
        </section>
      ))}

      <div className="en-contact">
        <p>For questions or requests regarding Friends of Standing Together membership, please contact us:</p>
        <a className="en-btn en-btn--outline" href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
          Contact us
        </a>
      </div>

      <div className="en-band">
        <h2 className="en-band-title">Not yet ready to become a friend, but still want to support us?</h2>
        <button type="button" className="en-btn en-btn--outline" onClick={showChooser}>Donate here</button>
      </div>
    </div>
  );
}
